import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { OrchestrationAoiMapComponent } from './orchestration-aoi-map';
import {
  GEOMETRY_FILE_ACCEPT,
  assignmentGeoJsonToWkt,
  assignmentWktToGeoJson,
  parseAssignmentGeometryFile,
} from '@app/pages/admin/orchestration-geometry-map.util';

@Component({
  selector: 'app-orchestration-geometry-map',
  standalone: true,
  imports: [TranslateModule, SecondaryButton, OrchestrationAoiMapComponent],
  templateUrl: './orchestration-geometry-map.html',
  styleUrl: './orchestration-geometry-map.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationGeometryMapComponent {
  readonly geoJson = input('');
  readonly geoJsonChange = output<string>();
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('geometryFile');

  readonly accept = GEOMETRY_FILE_ACCEPT;
  readonly fileName = signal('');
  readonly errorMessage = signal('');
  readonly mapWkt = signal('');
  readonly mapPolygonJson = computed(() => this.mapWkt() || assignmentGeoJsonToWkt(this.geoJson()) || undefined);

  constructor() {
    effect(() => {
      const incoming = this.geoJson();
      const fromMap = assignmentWktToGeoJson(this.mapWkt());
      if (!incoming) {
        if (this.mapWkt()) {
          this.mapWkt.set('');
        }
        return;
      }
      if (incoming === fromMap) {
        return;
      }
      const wkt = assignmentGeoJsonToWkt(incoming);
      if (wkt) {
        this.mapWkt.set(wkt);
      }
    }, { allowSignalWrites: true });
  }

  openFilePicker(): void {
    this.fileInput()?.nativeElement.click();
  }

  onMapWkt(wkt: string): void {
    this.errorMessage.set('');
    if (!wkt.trim()) {
      this.mapWkt.set('');
      this.geoJsonChange.emit('');
      return;
    }
    const geoJson = assignmentWktToGeoJson(wkt);
    if (!geoJson) {
      this.errorMessage.set('Could not read KML or GeoJSON');
      return;
    }
    this.mapWkt.set(wkt);
    this.geoJsonChange.emit(geoJson);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    void file.text().then(text => {
      const geoJson = parseAssignmentGeometryFile(file, text);
      if (!geoJson) {
        this.errorMessage.set('Could not read KML or GeoJSON');
        return;
      }
      this.fileName.set(file.name);
      this.errorMessage.set('');
      this.mapWkt.set(assignmentGeoJsonToWkt(geoJson) ?? '');
      this.geoJsonChange.emit(geoJson);
    }).catch(() => {
      this.errorMessage.set('Could not read KML or GeoJSON');
    });
  }
}
